if (!customElements.get("low-power-video")) {
  customElements.define(
    "low-power-video",
    class LowPowerVideo extends HTMLElement {
      constructor() {
        super();
        this.video_section = null;
        this.video_background_section = null;
        this.template = null;
        this.templates = {};
        this.observer = null;
        this.play_handlers = [];
        this.rendered_nodes = [];
        this.render_anchor = null;
        this.current_breakpoint = null;
        this.handle_resize = null;
        this.template_loaded = false;
      }

      connectedCallback() {
        this.template = this.querySelector("template");

        if (this.template) {
          this.collectTemplates();
          this.observeTemplate();
          return;
        }

        this.checkAndPlayVideoSections();
      }

      collectTemplates() {
        const templates = Array.from(this.querySelectorAll("template"));

        templates.forEach((template) => {
          const breakpoint = template.dataset.breakpoint || "default";

          this.templates[breakpoint] = template.innerHTML;
        });

        this.render_anchor = document.createComment("low-power-video");
        templates[0].before(this.render_anchor);
      }

      observeTemplate() {
        if (!("IntersectionObserver" in window)) {
          this.loadTemplate();
          return;
        }

        const section = this.closest("section") || this;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
                this.loadTemplate();
              }
            });
          },
          {
            threshold: [0, 0.15],
          },
        );

        this.observer.observe(section);
      }

      loadTemplate() {
        if (this.template_loaded) {
          return;
        }

        this.template_loaded = true;

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.template) {
          this.querySelectorAll("template").forEach((template) => {
            template.remove();
          });
          this.template = null;
        }

        this.renderTemplate();
        this.setAttribute("loaded", "");
        this.dispatchEvent(new CustomEvent("loaded"));
        this.addResizeListener();
      }

      getBreakpoint() {
        return window.innerWidth < 768 ? "mobile" : "desktop";
      }

      getTemplateHTML() {
        const breakpoint = this.getBreakpoint();

        this.current_breakpoint = breakpoint;

        return (
          this.templates[breakpoint] ||
          this.templates.default ||
          this.templates.desktop ||
          this.templates.mobile ||
          ""
        );
      }

      renderTemplate(autoplay = false) {
        const html = this.getTemplateHTML();
        const fragment = document.createRange().createContextualFragment(html);

        this.clearPlayHandlers();
        this.clearRenderedNodes();

        this.rendered_nodes = Array.from(fragment.childNodes);
        this.render_anchor.after(fragment);

        this.checkAndPlayVideoSections();

        if (autoplay) {
          this.playVideos();
        }
      }

      addResizeListener() {
        if (this.handle_resize) {
          return;
        }

        this.handle_resize = () => {
          const breakpoint = this.getBreakpoint();

          if (breakpoint !== this.current_breakpoint) {
            this.renderTemplate(true);
          }
        };

        window.addEventListener("resize", this.handle_resize);
      }

      checkAndPlayVideoSections() {
        this.video_section = this.querySelector(".wt-video");
        this.video_background_section = this.querySelector(
          ".hero--video-background",
        );
        if (this.video_section) {
          this.checkAndPlayVideoSection(this.video_section);
        } else if (this.video_background_section) {
          this.checkAndPlayBackgroundVideoSection(
            this.video_background_section,
          );
        } else if (this.querySelector("video")) {
          this.checkAndPlayVideoSection(this);
        }
      }

      checkAndPlayBackgroundVideoSection(video_background_section) {
        const hero_overlay =
          video_background_section.querySelector(".hero__overlay");
        const video_element = video_background_section.querySelector("video");
        if (hero_overlay && video_element) {
          this.addPlayHandler(hero_overlay, "touchstart", video_element);
          this.addPlayHandler(hero_overlay, "click", video_element);
        }
      }

      checkAndPlayVideoSection(video) {
        const video_element = video.querySelector("video");
        if (video_element) {
          this.addPlayHandler(video, "touchstart", video_element);
          this.addPlayHandler(video, "click", video_element);
        }
      }

      addPlayHandler(target, event_name, video_element) {
        const handler = () => this.playVideo(video_element);

        target.addEventListener(event_name, handler);
        this.play_handlers.push({ target, event_name, handler });
      }

      clearPlayHandlers() {
        this.play_handlers.forEach(({ target, event_name, handler }) => {
          target.removeEventListener(event_name, handler);
        });
        this.play_handlers = [];
      }

      clearRenderedNodes() {
        this.rendered_nodes.forEach((node) => {
          node.remove();
        });
        this.rendered_nodes = [];
      }

      playVideo(video_element) {
        if (!video_element.playing) {
          const play_promise = video_element.play();

          if (play_promise) {
            play_promise.catch(() => {});
          }
        }
      }

      playVideos() {
        this.querySelectorAll("video").forEach((video_element) => {
          this.playVideo(video_element);
        });
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.handle_resize) {
          window.removeEventListener("resize", this.handle_resize);
          this.handle_resize = null;
        }

        this.clearPlayHandlers();
      }
    },
  );
}
